import { Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import Alert from '@components/Alert';
import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import Icon from '@components/Icon/Icon';

import { AssessmentResultProvider } from '@contexts/assessment-result';

import { fromSharedAssessmentResult } from '@models/assessment/result';
import type { SharedAssessmentResult as SharedAssessmentResultRecord } from '@models/assessment/shared';

import AssessmentDrilldown from '@views/Assessments/Bha/Drilldown';

const messages = defineMessages({
  export: {
    id: 'sharedAssessment.export',
    defaultMessage: 'Export Report',
  },
  unavailableTitle: {
    id: 'sharedAssessment.unavailable',
    defaultMessage: 'Result unavailable',
  },
  unavailableBody: {
    id: 'sharedAssessment.unavailableDescription',
    defaultMessage:
      'This assessment could not be loaded. Ask the sender to share it again.',
  },
});

interface SharedAssessmentResultProps {
  record?: SharedAssessmentResultRecord | null;
  loading: boolean;
}

/**
 * Read-only DHA result for the public share-link view.
 *
 * Deliberately has no Share action and no navigation -- those only make sense
 * for an authenticated owner, not an anonymous recipient of a shared link. The
 * result body itself is the same tree the authenticated drilldown renders, fed
 * through AssessmentResultProvider's pre-resolved mode so no authenticated
 * query is issued.
 *
 * Export is a plain anchor onto a short-lived presigned URL the backend minted
 * for this share's own assessment -- the recipient never receives an S3 key.
 */
function SharedAssessmentResult({
  record,
  loading,
}: SharedAssessmentResultProps) {
  const { formatMessage } = useIntl();
  const result = React.useMemo(
    () => fromSharedAssessmentResult(record),
    [record]
  );

  /**
   * Fail loudly rather than rendering an empty shell.
   *
   * `transformResource` maps anything it does not recognise to `null`, so a
   * drifted or malformed `resourceType` arrives here as a missing record. A
   * record with no risk assessment is the same story from the user's side:
   * every metric would render as "-" under a full page of section headings,
   * which reads as "you have no results" rather than "this did not load".
   */
  if (!loading && !result.riskAssessment) {
    return (
      <Alert
        severity="error"
        variant="standard"
        title={formatMessage(messages.unavailableTitle)}
      >
        <Typography variant="subtitle1">
          {formatMessage(messages.unavailableBody)}
        </Typography>
      </Alert>
    );
  }

  return (
    <ContentLoader loading={loading}>
      <Stack spacing={2}>
        {record?.export_url && (
          <Stack alignItems="flex-end">
            {/*
              No `download` attribute: this href is a cross-origin presigned S3
              URL, where browsers ignore it outright. The download is driven by
              the object's own `Content-Disposition: attachment; filename=...`,
              which completeAssessment sets at upload time and getS3AssetRecord
              preserves (useOriginalContentDisposition).

              `target="_blank"` stays: with an attachment disposition the
              transient tab closes itself once the download starts, and if the
              presigned URL has expired the S3 error lands in that throwaway tab
              instead of replacing the shared result.
            */}
            <Button
              component="a"
              href={record.export_url}
              rel="noopener noreferrer"
              startIcon={<Icon name="download" type="fas" />}
              target="_blank"
            >
              {formatMessage(messages.export)}
            </Button>
          </Stack>
        )}
        <AssessmentResultProvider loading={loading} result={result}>
          <AssessmentDrilldown />
        </AssessmentResultProvider>
      </Stack>
    </ContentLoader>
  );
}

export default withErrorBoundary(SharedAssessmentResult, {
  FallbackComponent: ErrorBoundaryFallback,
});
